import React, { useContext, useState } from "react";
import * as Yup from "yup";
import {
  Alert,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { CreateUser } from "../../service/UserService";
import eye from "../../assets/icons/eye.svg";
import eyeslash from "../../assets/icons/eye-slash.svg";
import { ReactComponent as SmsIcon } from "../../assets/icons/sms.svg";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";
import { Navigation } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { PreferencesContext } from "../../PreferenceContext";
import { AlertType } from "../../shared/AlertType";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import LayoutBg from "../../assets/images/layoutBg.png";
import logo from "../../assets/images/redlogo.png"; 

const formInitialValues = {
  firstName: "",
  organizationName: "",
  email: "",
  password: "",
  passwordConfirmation: "",
};

export default function SignUp() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ isVisible: false, message: "" });
  const [values, setValues] = useState(formInitialValues);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    organizationName: Yup.string().nullable(),
    email: Yup.string().email("Invalid email").required("Email is required"),
    // password: Yup.string().required("Password is required"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Must contain at least one uppercase letter")
      .matches(/[a-z]/, "Must contain at least one lowercase letter")
      .matches(/[0-9]/, "Must contain at least one number")
      .matches(/[@$!%*?&#]/, "Must contain at least one special character"),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Confirm password is required"),
  });

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues({ ...values, [field]: event.target.value });
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await validationSchema.validate(values, { abortEarly: false });
      setErrors({});
      await submitForm();
    } catch (validationErr: any) {
      const formErrors: Record<string, string> = {};
      validationErr.inner.forEach((err: any) => {
        if (err.path) formErrors[err.path] = err.message;
      });
      setErrors(formErrors);
    }
  };

  const submitForm = async () => {
    setLoading(true);
    try {
      const data = {
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        organizationName: values.organizationName,
        userRole: "USER",
      };
      const response: any = await CreateUser(data);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "User created successful",
        alertType: AlertType.Success,
      });
      setValues(formInitialValues);
      navigate("/sign-in");
    } catch (error: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Email already exists",
        alertType: AlertType.Error,
      });
      // setAlert({
      //   isVisible: true,
      //   message: error.message || "Error creating user",
      // });
    } finally {
      setLoading(false);
      setTimeout(() => setAlert({ isVisible: false, message: "" }), 5000);
    }
  };

  return (
    <Grid
      container
      sx={{
        position: "relative",
        backgroundColor: "#E5E5E5",
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        height: "100%",
        width: "100%",
        minWidth: "991px",
        alignItems: "center",
        minHeight: "100vh",
        overflowY: "auto",
        overflowX: "hidden",

        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${LayoutBg})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.1,
          zIndex: 0,
        },

        "& > *": {
          position: "relative",
          zIndex: 1,
        },
      }}
    >
      <Box>
        <Grid size={12} textAlign="center" sx={{ marginBottom: "5px" }}>
          <a href="https://www.stakeholdermaps.com/">
            <img src={logo} alt="logo" style={{ maxWidth: 200 }} />
          </a>
        </Grid>
        <Paper elevation={0} className="paper-singin">
          <Grid sx={{ width: "100%" }}>
            <form onSubmit={handleSubmit}>
              <Box className="box-container" sx={{ my: 2 }}>
                <Paper className="paper-container">
                  {/* <Box>
                    <Link className="backspace-Button" to={"/sign-in"}>
                      <KeyboardBackspaceIcon className="icon-css" />
                      <b>Back</b>
                    </Link>
                  </Box> */}
                  <Typography
                    style={{
                      fontSize: "22px",
                      fontWeight: 400,
                      paddingBottom: "10px",
                    }}
                  >
                    Register your Free account
                  </Typography>

                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: "400px",
                      color: "rgba(96, 96, 96, 1)",
                      paddingBottom: "10px",
                    }}
                  >
                    Already have an account?{" "}
                    <Link to="/sign-in" style={{ textDecoration: "underline" }}>
                      Sign in
                    </Link>
                  </Typography>

                  {/* {alert.isVisible && (
                    <Box marginBottom={1}>
                      <Alert severity="success">{alert.message}</Alert>
                    </Box>
                  )} */}

                  <Box className="signup-fields">
                    <Typography>Full Name</Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      placeholder="John doe"
                      value={values.firstName}
                      onChange={handleChange("firstName")}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                  </Box>

                  <Box className="signup-fields">
                    <Typography>Organization Name</Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      placeholder="Company"
                      value={values.organizationName}
                      onChange={handleChange("organizationName")}
                      error={!!errors.organizationName}
                      helperText={errors.organizationName}
                    />
                  </Box>

                  <Box className="signup-fields">
                    <Typography>Email address</Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      placeholder="name@company.com"
                      value={values.email}
                      onChange={handleChange("email")}
                      error={!!errors.email}
                      helperText={errors.email}
                      // InputProps={{
                      //   startAdornment: (
                      //     <InputAdornment position="start">
                      //       <SmsIcon stroke={"red"} />
                      //     </InputAdornment>
                      //   ),
                      // }}
                    />
                  </Box>

                  <Box className="signup-fields">
                    <Typography>Create password</Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter strong password"
                      value={values.password}
                      onChange={handleChange("password")}
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        // startAdornment: (
                        //   <InputAdornment position="start">
                        //     <LockIcon />
                        //   </InputAdornment>
                        // ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{
                                padding: "0 !important",
                              }}
                            >
                              <img
                                loading="lazy"
                                alt={showPassword ? "hide" : "show"}
                                className="eye-icon"
                                src={showPassword ? eyeslash : eye}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box className="signup-fields">
                    <Typography>Confirm Password</Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm Password"
                      value={values.passwordConfirmation}
                      onChange={handleChange("passwordConfirmation")}
                      error={!!errors.passwordConfirmation}
                      helperText={errors.passwordConfirmation}
                      InputProps={{
                        // startAdornment: (
                        //   <InputAdornment position="start">
                        //     <LockIcon />
                        //   </InputAdornment>
                        // ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                              sx={{
                                padding: "0 !important",
                              }}
                            >
                              <img
                                loading="lazy"
                                alt={showConfirmPassword ? "hide" : "show"}
                                className="eye-icon"
                                src={showConfirmPassword ? eyeslash : eye}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <LoadingButton
                    color="primary"
                    loading={loading}
                    variant="contained"
                    type="submit"
                    fullWidth
                    sx={{
                      marginTop: 3,
                      backgroundColor: "#4C0808",
                      fontWeight: 400,
                      fontSize: "13px",
                      borderRadius: "4px",
                    }}
                  >
                    Sign up
                  </LoadingButton>
                </Paper>
              </Box>
            </form>
          </Grid>
          {/* <Grid className="img-wrapper"></Grid> */}
        </Paper>
        <Grid
          size={12}
          textAlign="center"
          mt={2}
          style={{
            fontSize: "12px",
            fontWeight: 400,
            color: "rgba(96, 96, 96, 1)",
          }}
        >
          By creating an account, you agree to <br />
          our{" "}
          <Link
            to={"/#"}
            style={{
              textDecoration: "underline",
              color: "rgba(96, 96, 96, 1)",
            }}
          >
            Privacy Policy{" "}
          </Link>{" "}
          and{" "}
          <Link
            to={"/#"}
            style={{
              textDecoration: "underline",
              color: "rgba(96, 96, 96, 1)",
            }}
          >
            Terms of Service
          </Link>
          .
        </Grid>
      </Box>
    </Grid>
  );
}
